import { Helmet } from "react-helmet";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2";
import { Autocomplete, Button, Chip, IconButton, Tooltip } from "@mui/material";
import CustomTableContainer from "@/table//CustomTableContainer";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import { CustomRow } from "@/table/CustomTable";
import { statusQueryToFilterValue, queryToFilterValue } from "@/utils";
import {
    ACCOUNT_TYPES,
    BUSINESS_TYPES,
    VENDOR_STATUS_COLORS,
    getVendorStatusColor,
} from "./constants";
import { AddCircle } from "@mui/icons-material";
import { useEffect, useState } from "react";
import VendorPopup from "./VendorPopup";
import EditIcon from "@mui/icons-material/Edit";
import { request } from "@/Api";
import { useParams } from "react-router-dom";
import LikeButton from "@/common/LikeButton";
import VendorInvitationsPopup from "./VendorInvitationsPopup";

export default function Vendors() {
    const { vendorId } = useParams();
    const [popupOpen, setPopupOpen] = useState(false);
    const [activeVendor, setActiveVendor] = useState(null);

    const handleOpenDefault = () => {
        if (!vendorId) {
            return;
        }
        handleVendorEdit(vendorId);
    };

    const handleVendorAdd = () => {
        setActiveVendor({});
        setPopupOpen(true);
    };

    const handleVendorEdit = (vendorId) => {
        request.get(`/vendors/${vendorId}`).then((response) => {
            const fullVendor = response.data;
            setActiveVendor(fullVendor);
            setPopupOpen(true);
        });
    };

    const handleLikeToggle = (vendor, updateVendor) => (liked) => {
        request
            .post("like-button", { model_type: "vendor", model_id: vendor.id })
            .then((_) => {
                const newLikesCount = vendor.likes_count + (liked ? +1 : -1);
                updateVendor(vendor.id, {
                    ...vendor,
                    liked: liked,
                    likes_count: newLikesCount,
                });
            });
    };

    useEffect(() => {
        handleOpenDefault(vendorId);
    }, [vendorId]);

    return (
        <>
            <CustomTableContainer
                modelType="vendor"
                defaultOrder="-id"
                title="Vendors"
                headCells={[
                    { id: "email", label: "Email" },
                    { id: "name", label: "Name" },
                    { id: "status", label: "Status" },
                    { id: "business_type", label: "Type" },
                    { id: "expertise", label: "Expertise" },
                    { id: "city_name", label: "Location" },
                    { id: "projects_completed", label: "Project Completed" },
                    { id: "_likes_count", label: "Favorites" },
                    { id: "_actions", label: "" },
                ]}
                itemRowComponent={({ item, updateItem }) => (
                    <CustomRow>
                        <TableCell align="left">{item.email}</TableCell>
                        <TableCell component="th" scope="row">
                            {item.name}
                        </TableCell>
                        <TableCell align="left">
                            <Tooltip title={item.status_reason || ""}>
                                <Chip
                                    variant="outlined"
                                    label={item.status}
                                    color={getVendorStatusColor(item.status)}
                                />
                            </Tooltip>
                        </TableCell>
                        <TableCell align="left">{item.business_type}</TableCell>
                        <TableCell align="left">{item.expertise}</TableCell>
                        <TableCell align="left">{item.city?.name}</TableCell>
                        <TableCell align="left">
                            {item.projects_completed}
                        </TableCell>
                        <TableCell align="left">
                            <Grid container alignItems={"center"} spacing={1}>
                                <Grid item marginRight={1}>
                                    <LikeButton
                                        defaultLiked={item.liked}
                                        onToggle={handleLikeToggle(
                                            item,
                                            updateItem,
                                        )}
                                    />
                                </Grid>
                                <Grid item>
                                    <span style={{ fontSize: 12 }}>
                                        {item.likes_count}
                                    </span>
                                </Grid>
                            </Grid>
                        </TableCell>
                        <TableCell align="left">
                            <Grid
                                container
                                width={100}
                                justifyContent={"right"}
                                spacing={1}
                                alignItems={"center"}
                            >
                                <Grid item>
                                    <IconButton
                                        onClick={() => {
                                            handleVendorEdit(item.id);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </TableCell>
                    </CustomRow>
                )}
                filterComponents={({
                    filters,
                    setFilters,
                    handleInputChange,
                    handleAutocompleteChange,
                    handleAutocompleteClear,
                }) => {
                    const handleMultiAutoCompleteChange =
                        (filterName) => (event, newValue) => {
                            if (newValue && newValue.length > 0) {
                                setFilters({
                                    ...filters,
                                    [filterName]:
                                        "has_any:" + newValue.join(","),
                                });
                            }
                        };
                    return (
                        <Grid container sx={{ paddingTop: 2 }} spacing={3}>
                            <Grid xs={6} md={3}>
                                <ApiAutocomplete
                                    label="Location"
                                    sx={{ paddingBottom: 2 }}
                                    value={queryToFilterValue(filters.city_id)}
                                    apiRoute={"locations"}
                                    fields={["full_name"]}
                                    getOptionLabel={(option) =>
                                        option.full_name
                                    }
                                    onChange={handleAutocompleteChange(
                                        "city_id",
                                    )}
                                    onInputChange={handleAutocompleteClear(
                                        "city_id",
                                    )}
                                    onInputResponseLimit={30}
                                />
                            </Grid>
                            <Grid xs={6} md={3}>
                                <Autocomplete
                                    value={queryToFilterValue(
                                        filters.business_type,
                                    )}
                                    disablePortal
                                    options={BUSINESS_TYPES}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Type" />
                                    )}
                                    onChange={handleAutocompleteChange(
                                        "business_type",
                                    )}
                                    onInputChange={handleAutocompleteClear(
                                        "business_type",
                                    )}
                                />
                            </Grid>
                            <Grid xs={6} md={3}>
                                <Autocomplete
                                    value={queryToFilterValue(filters.status)}
                                    disablePortal
                                    options={Object.keys(VENDOR_STATUS_COLORS)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Status" />
                                    )}
                                    onChange={handleAutocompleteChange(
                                        "status",
                                    )}
                                    onInputChange={handleAutocompleteClear(
                                        "status",
                                    )}
                                />
                            </Grid>
                            <Grid xs={6} md={3}>
                                <ApiAutocomplete
                                    label="Skills"
                                    value={statusQueryToFilterValue(
                                        filters.skill_ids,
                                    )}
                                    apiRoute={"skills"}
                                    fields={["name"]}
                                    onChange={handleMultiAutoCompleteChange(
                                        "skill_ids",
                                    )}
                                    onInputChange={handleAutocompleteClear(
                                        "skill_ids",
                                    )}
                                    isOptionEqualToValue={(option, values) =>
                                        values.includes(option)
                                    }
                                    multiple={true}
                                />
                            </Grid>
                        </Grid>
                    );
                }}
                customBottomLeftComponent={({
                    addItem,
                    addItems,
                    updateItem,
                }) => (
                    <>
                        <Grid container width={1000} spacing={1} zIndex={1}>
                            <Grid item>
                                <VendorInvitationsPopup
                                    onInvite={(newVendors) => {
                                        addItems(newVendors);
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    color="inherit"
                                    startIcon={<AddCircle />}
                                    onClick={handleVendorAdd}
                                >
                                    Add Vendor
                                </Button>
                            </Grid>
                        </Grid>
                        <VendorPopup
                            vendorData={activeVendor}
                            isOpen={popupOpen}
                            close={() => {
                                setPopupOpen(false);
                            }}
                            addVendor={addItem}
                            updateVendor={updateItem}
                        />
                    </>
                )}
            >
                <Helmet>
                    <title>Vendors - Makers Central</title>
                </Helmet>
            </CustomTableContainer>
        </>
    );
}
